import { useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { createMemoCell } from '@/lib/create-memo-cell'
import {
  RelationshipEditor,
  useRelationshipEditor,
  type RelationshipItem,
} from './relationship-editor'
import { CellBadge } from './cell-badge'
import { useUsersSearch } from '@/hooks/use-reference-data'
import { useUpdateCompany } from '@/hooks/use-companies'
import { showSuccess, showError } from '@/lib/toast'
import type { UpdateCompany } from '@/types/api'
import { cn } from '@/lib/utils'
import { useCanEditCompany } from '@/hooks/use-permissions'

type OwnerFieldType = 'primaryOwner' | 'secondaryOwner'

interface OwnerCellProps {
  companyId: string
  primaryOwnerId?: string | null
  secondaryOwnerId?: string | null
  fieldType: OwnerFieldType
  currentOwner: { id: string; name: string } | null
}

const FIELD_CONFIG: Record<
  OwnerFieldType,
  { updateKey: keyof UpdateCompany; label: string }
> = {
  primaryOwner: { updateKey: 'primaryOwnerId', label: 'Primary owner' },
  secondaryOwner: { updateKey: 'secondaryOwnerId', label: 'Secondary owner' },
}

function OwnerCellBase({
  companyId,
  primaryOwnerId,
  secondaryOwnerId,
  fieldType,
  currentOwner,
}: OwnerCellProps) {
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(primaryOwnerId, secondaryOwnerId)
  const { open, setOpen, searchTerm, setSearchTerm, debouncedSearch } =
    useRelationshipEditor()

  // Optimistic state with prop-tracking to clear when server data arrives
  // React pattern: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [optimisticOwner, setOptimisticOwner] = useState<
    RelationshipItem | null | undefined
  >(undefined)
  const [prevOwnerId, setPrevOwnerId] = useState(currentOwner?.id)

  if (currentOwner?.id !== prevOwnerId) {
    setPrevOwnerId(currentOwner?.id)
    setOptimisticOwner(undefined)
  }

  const mutationIdRef = useRef(0)

  const { data: usersData, isLoading: usersLoading } = useUsersSearch({
    search: debouncedSearch,
    enabled: open,
  })

  const updateCompany = useUpdateCompany()

  const config = FIELD_CONFIG[fieldType]

  // Exclude the user who is already the other owner
  const otherOwnerId =
    fieldType === 'primaryOwner' ? secondaryOwnerId : primaryOwnerId
  const searchData: RelationshipItem[] | undefined = usersData?.data
    ?.filter((u) => u.id !== otherOwnerId)
    .map((u) => ({
      id: u.id,
      name: u.name,
    }))

  const displayOwner =
    optimisticOwner !== undefined ? optimisticOwner : currentOwner
  const currentValues: RelationshipItem[] = displayOwner ? [displayOwner] : []

  const handleAdd = (item: RelationshipItem) => {
    const mutId = ++mutationIdRef.current
    setOptimisticOwner(item)

    const data = { [config.updateKey]: item.id } as Partial<UpdateCompany>
    updateCompany.mutate(
      { id: companyId, data, optimistic: false },
      {
        onSuccess: () => showSuccess(`${config.label} updated`),
        onError: (error) => {
          if (mutationIdRef.current === mutId) {
            setOptimisticOwner(undefined)
          }
          showError(error, `Failed to update ${config.label.toLowerCase()}`)
        },
      }
    )
  }

  const handleRemove = () => {
    const mutId = ++mutationIdRef.current
    setOptimisticOwner(null)

    const data = { [config.updateKey]: null } as Partial<UpdateCompany>
    updateCompany.mutate(
      { id: companyId, data, optimistic: false },
      {
        onSuccess: () => showSuccess(`${config.label} removed`),
        onError: (error) => {
          if (mutationIdRef.current === mutId) {
            setOptimisticOwner(undefined)
          }
          showError(error, `Failed to remove ${config.label.toLowerCase()}`)
        },
      }
    )
  }

  const isEmpty = !displayOwner

  if (!canEdit) {
    if (isEmpty) {
      return (
        <Typography variant="body-sm" className="text-muted-foreground">
          -
        </Typography>
      )
    }
    return <CellBadge value={displayOwner.name} variant="solid" />
  }

  return (
    <RelationshipEditor
      currentValues={currentValues}
      isMulti={false}
      onAdd={handleAdd}
      onRemove={handleRemove}
      searchData={searchData}
      isSearchLoading={usersLoading}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      placeholder="Search team members..."
      open={open}
      onOpenChange={setOpen}
    >
      {isEmpty ? (
        <Button
          variant="ghost"
          className={cn(
            'group w-full flex items-center justify-between h-auto px-2',
            'hover:bg-muted/50'
          )}
        >
          <Typography variant="body-sm" className="text-muted-foreground">
            -
          </Typography>
          <Pencil className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          className={cn(
            'group w-full flex items-center justify-between h-auto px-2',
            'hover:bg-muted/50'
          )}
        >
          <CellBadge value={displayOwner.name} variant="solid" />
          <Pencil className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </Button>
      )}
    </RelationshipEditor>
  )
}

export const OwnerCell = createMemoCell(OwnerCellBase, 'OwnerCell')
